import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'app-ai-orb',
  template: `<canvas #canvas></canvas>`,
  styleUrls: ['./ai-orb.component.css'],
})
export class AiOrbComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  private ctx!: CanvasRenderingContext2D;
  private animationId: number | null = null;

/** 🎨 Base gradient colors input (one source of truth) */
@Input() baseGradientColors: string[] = [
  'rgba(20, 186, 203, 0.5)',
  'rgba(57, 122, 241, 0.83)',
  'rgba(176, 102, 205, 0.93)',
  'rgba(247, 82, 120, 0.5)',
  'rgba(245, 95, 92, 0.5)',
];

/** 🌈 Outer orb gradient (autogenerated) */
orbGradientStops: { position: number; color: string }[] = [];

/** 🌕 Inner orb gradient (autogenerated, lighter core) */
orbCoreStops: { position: number; color: string }[] = [];

  /** ⏱️ Transition duration in ms (for smooth preset changes) */
  @Input() transitionDuration: number = 1500;

  /** 🎚️ Animation controls */
  @Input() animationMode: 'idle' | 'thinking' | 'writing' = 'idle';

  
  

  /** ⚙️ Extra controllable options */
  @Input() gradientAngle: number = 98;
  @Input() gradientOpacity: number = 0.35;
  @Input() useRadialGradient: boolean = false;


  /** 🎚️ External size control (scales both canvas + orb) */
  size: number = 100
  @Input('size') set setSize(size: number) {
    if (size) { this.size = size; }
    this.canvasSize = this.size;
    this.orbRadius = this.size * 0.35;
  }

  canvasSize: number = this.size;
  orbRadius: number = this.size * 0.35;

  // Orb state
  private dpr = window.devicePixelRatio || 1;
  private pi = Math.PI;
  private points = 12;
  private center = { x: 0, y: 0 };
  private circles: any[] = [];
  private tick = 0;
  private voiceIntensity = 0;
  private targetIntensity = 0;
  private orbSizeMultiplier = 0.9;
  private currentOrbRadius = this.orbRadius * 0.9;

  // 🎯 Transition state tracking
  private transitionStartTime: number = 0;
  private isTransitioning: boolean = false;

  // Current interpolated values
  private currentPulseSpeed: number = 0.4;
  private currentRandomness: number = 0.4;
  private currentRotationSpeed: number = 0.15;

  // Previous values (for interpolation)
  private prevPulseSpeed: number = 0.4;
  private prevRandomness: number = 0.4;
  private prevRotationSpeed: number = 0.15;
  private prevIntensityTarget: number = 0.05;
  private prevOrbGradientStops: any[] = [];
  private prevOrbCoreStops: any[] = [];

  private modeConfigs = {
    idle: {
      pulseSpeed: 0.4,
      randomness: 0.4,
      rotationSpeed: 0.15,
      intensityTarget: 0.05,
      orbGradientStops: [
        { position: 0, color: 'rgba(20, 186, 203, 0.4)' },
        { position: 25, color: 'rgba(57, 122, 241, 0.5)' },
        { position: 50, color: 'rgba(176, 102, 205, 0.4)' },
        { position: 75, color: 'rgba(247, 82, 120, 0.3)' },
        { position: 100, color: 'rgba(245, 95, 92, 0.25)' }
      ],
      orbCoreStops: [
        { position: 0, color: '#D4FAFA' },
        { position: 50, color: '#E5D4FB' },
        { position: 100, color: '#FDD6CC' }
      ]
    },
    thinking: {
      pulseSpeed: 0.8,
      randomness: 1.2,
      rotationSpeed: 0.3,
      intensityTarget: 0.15,
      orbGradientStops: [
        { position: 0, color: 'rgba(20, 186, 203, 0.6)' },
        { position: 25, color: 'rgba(57, 122, 241, 0.7)' },
        { position: 50, color: 'rgba(176, 102, 205, 0.75)' },
        { position: 75, color: 'rgba(247, 82, 120, 0.6)' },
        { position: 100, color: 'rgba(245, 95, 92, 0.55)' }
      ],
      orbCoreStops: [
        { position: 0, color: '#C8FAFA' },
        { position: 50, color: '#D8C8F9' },
        { position: 100, color: '#FBC3BA' }
      ]
    },
    writing: {
      pulseSpeed: 1.2,
      randomness: 0.9,
      rotationSpeed: 0.5,
      intensityTarget: 0.3,
      orbGradientStops: [
        { position: 0, color: 'rgba(20, 186, 203, 0.6)' },
        { position: 25, color: 'rgba(57, 122, 241, 0.7)' },
        { position: 50, color: 'rgba(176, 102, 205, 0.75)' },
        { position: 75, color: 'rgba(247, 82, 120, 0.6)' },
        { position: 100, color: 'rgba(245, 95, 92, 0.55)' }
      ],
      orbCoreStops: [
        { position: 0, color: '#8EF7F7' },
        { position: 50, color: '#D8C8F9' },
        { position: 100, color: '#FBC3BA' }
      ]
    }
  };
  ngAfterViewInit() {
    this.ctx = this.canvasRef.nativeElement.getContext('2d')!;
    this.setupCanvasSize();
    this.observeResize();
    this.generateGradients(); // ✅ initialize colors
    this.initOrb();
    this.animate();
  }
  

  
  /** 🎯 Initialize orb state */
  /** 🔮 Initialize orb and canvas with proper size + state */
  private initOrb() {
    this.applySize(); // ✅ rely only on Input size
  
    this.tick = 0;
    this.voiceIntensity = 0;
    this.targetIntensity = 0;
    this.orbSizeMultiplier = 0.9;
    this.currentOrbRadius = this.orbRadius * 0.9;
  
    // Init swingpoints
    this.circles = [];
    for (let idx = 0; idx < 3; idx++) {
      const swingpoints: any[] = [];
      for (let i = 0; i < this.points; i++) {
        const radian = (this.pi * 2 / this.points) * i;
        swingpoints.push({
          x: this.center.x + this.orbRadius * 0.9 * Math.cos(radian),
          y: this.center.y + this.orbRadius * 0.9 * Math.sin(radian),
          radian,
          range: this.random(5, 15),
          phase: this.random(0, this.pi * 2),
        });
      }
      this.circles.push(swingpoints);
    }
  }

  /** 🔍 Watch container resize to keep canvas accurate */
  private observeResize() {
    const container = this.canvasRef.nativeElement.parentElement!;
    const resizeObserver = new ResizeObserver(() => {
      this.setupCanvasSize();
    });
    resizeObserver.observe(container);
  }

  /** 🎯 Setup canvas to match container + DPR */
  private setupCanvasSize() {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.parentElement!.getBoundingClientRect();

    this.canvasSize = Math.min(rect.width, rect.height, this.size); // keep within parent
    this.orbRadius = this.canvasSize * 0.35;

    canvas.width = this.canvasSize * this.dpr;
    canvas.height = this.canvasSize * this.dpr;
    canvas.style.width = this.canvasSize + 'px';
    canvas.style.height = this.canvasSize + 'px';

    this.ctx.setTransform(1, 0, 0, 1, 0, 0); // reset transform
    this.ctx.scale(this.dpr, this.dpr);

    this.center = { x: this.canvasSize / 2, y: this.canvasSize / 2 };
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes['size'] && !changes['size'].firstChange) {
      this.applySize();
    }
    if (changes['animationMode'] && !changes['animationMode'].firstChange) {
      this.previousMode = changes['animationMode'].previousValue;
      this.startTransition();
    }
    if (changes['baseGradientColors'] && !changes['baseGradientColors'].firstChange) {
      this.generateGradients(); // ✅ regenerate gradients on input change
    }
  }

  /** 🖼️ Apply size dynamically */
  private applySize() {
    this.canvasSize = this.size;
    this.orbRadius = this.size * 0.35;
  
    const canvas = this.canvasRef.nativeElement;
    canvas.width = this.canvasSize * this.dpr;
    canvas.height = this.canvasSize * this.dpr;
    canvas.style.width = `${this.canvasSize}px`;
    canvas.style.height = `${this.canvasSize}px`;
  
    this.ctx.setTransform(1, 0, 0, 1, 0, 0); // reset
    this.ctx.scale(this.dpr, this.dpr);
  
    this.center = { x: this.canvasSize / 2, y: this.canvasSize / 2 };
  }

  ngOnDestroy() {
    if (this.animationId) cancelAnimationFrame(this.animationId);
  }

  /** 🔄 Start smooth transition to new mode */
  private startTransition() {
    const config = this.modeConfigs[this.animationMode];
    const oldConfig = this.modeConfigs[this.getPreviousMode()];

    // Store previous values
    this.prevPulseSpeed = this.currentPulseSpeed;
    this.prevRandomness = this.currentRandomness;
    this.prevRotationSpeed = this.currentRotationSpeed;
    this.prevIntensityTarget = this.targetIntensity;

    // Use actual previous mode's colors if available, otherwise use current interpolated colors
    if (this.prevOrbGradientStops.length === 0) {
      this.prevOrbGradientStops = JSON.parse(JSON.stringify(oldConfig.orbGradientStops));
      this.prevOrbCoreStops = JSON.parse(JSON.stringify(oldConfig.orbCoreStops));
    } else {
      // Preserve the current interpolated state
      this.prevOrbGradientStops = JSON.parse(JSON.stringify(this.prevOrbGradientStops));
      this.prevOrbCoreStops = JSON.parse(JSON.stringify(this.prevOrbCoreStops));
    }

    // Mark transition start
    this.transitionStartTime = performance.now();
    this.isTransitioning = true;
  }

  /** 📋 Track previous mode for smooth transitions */
  private previousMode: 'idle' | 'thinking' | 'writing' = 'idle';

  private getPreviousMode(): 'idle' | 'thinking' | 'writing' {
    return this.previousMode;
  }

  /** 📐 Easing function for smooth transitions (ease-in-out) */
  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /** 🎨 Interpolate color values */
  private interpolateColor(color1: string, color2: string, t: number): string {
    const rgba1 = this.parseColor(color1);
    const rgba2 = this.parseColor(color2);

    const r = Math.round(rgba1.r + (rgba2.r - rgba1.r) * t);
    const g = Math.round(rgba1.g + (rgba2.g - rgba1.g) * t);
    const b = Math.round(rgba1.b + (rgba2.b - rgba1.b) * t);
    const a = rgba1.a + (rgba2.a - rgba1.a) * t;

    return `rgba(${r},${g},${b},${a})`;
  }


  /** 🎯 Get interpolated values during transition */
  private getInterpolatedValues() {
    const config = this.modeConfigs[this.animationMode];

    if (!this.isTransitioning) {
      // Store current colors when not transitioning
      if (this.prevOrbGradientStops.length === 0) {
        this.prevOrbGradientStops = JSON.parse(JSON.stringify(config.orbGradientStops));
        this.prevOrbCoreStops = JSON.parse(JSON.stringify(config.orbCoreStops));
      }
      return {
        pulseSpeed: config.pulseSpeed,
        randomness: config.randomness,
        rotationSpeed: config.rotationSpeed,
        intensityTarget: config.intensityTarget,
        orbGradientStops: config.orbGradientStops,
        orbCoreStops: config.orbCoreStops,
      };
    }

    const elapsed = performance.now() - this.transitionStartTime;
    const progress = Math.min(elapsed / this.transitionDuration, 1);
    const easedProgress = this.easeInOutCubic(progress);

    // Complete transition
    if (progress >= 1) {
      this.isTransitioning = false;
      this.currentPulseSpeed = config.pulseSpeed;
      this.currentRandomness = config.randomness;
      this.currentRotationSpeed = config.rotationSpeed;
      // Store the final colors as previous for next transition
      this.prevOrbGradientStops = JSON.parse(JSON.stringify(config.orbGradientStops));
      this.prevOrbCoreStops = JSON.parse(JSON.stringify(config.orbCoreStops));
      return {
        pulseSpeed: config.pulseSpeed,
        randomness: config.randomness,
        rotationSpeed: config.rotationSpeed,
        intensityTarget: config.intensityTarget,
        orbGradientStops: config.orbGradientStops,
        orbCoreStops: config.orbCoreStops,
      };
    }

    // Interpolate numeric values
    this.currentPulseSpeed = this.prevPulseSpeed + (config.pulseSpeed - this.prevPulseSpeed) * easedProgress;
    this.currentRandomness = this.prevRandomness + (config.randomness - this.prevRandomness) * easedProgress;
    this.currentRotationSpeed = this.prevRotationSpeed + (config.rotationSpeed - this.prevRotationSpeed) * easedProgress;
    const interpolatedIntensity = this.prevIntensityTarget + (config.intensityTarget - this.prevIntensityTarget) * easedProgress;

    // Interpolate gradient stops
    const interpolatedOrbGradient = config.orbGradientStops.map((stop, i) => {
      const prevStop = this.prevOrbGradientStops[i];
      return {
        position: stop.position,
        color: prevStop ? this.interpolateColor(prevStop.color, stop.color, easedProgress) : stop.color,
      };
    });

    const interpolatedOrbCore = config.orbCoreStops.map((stop, i) => {
      const prevStop = this.prevOrbCoreStops[i];
      return {
        position: stop.position,
        color: prevStop ? this.interpolateColor(prevStop.color, stop.color, easedProgress) : stop.color,
      };
    });

    // Store interpolated colors for next frame
    this.prevOrbGradientStops = JSON.parse(JSON.stringify(interpolatedOrbGradient));
    this.prevOrbCoreStops = JSON.parse(JSON.stringify(interpolatedOrbCore));

    return {
      pulseSpeed: this.currentPulseSpeed,
      randomness: this.currentRandomness,
      rotationSpeed: this.currentRotationSpeed,
      intensityTarget: interpolatedIntensity,
      orbGradientStops: interpolatedOrbGradient,
      orbCoreStops: interpolatedOrbCore,
    };
  }

  private animate = () => {
    const ctx = this.ctx;
    const w = this.canvasSize;
    const h = this.canvasSize;
    ctx.clearRect(0, 0, w, h);

    // 🎛 Get interpolated values
    const {
      pulseSpeed,
      randomness,
      rotationSpeed,
      intensityTarget,
      orbGradientStops,
      orbCoreStops,
    } = this.getInterpolatedValues();

    const layerStops = orbGradientStops;
    const coreStops = orbCoreStops;

    // breathing & pulse
    this.targetIntensity = intensityTarget;
    this.voiceIntensity += (this.targetIntensity - this.voiceIntensity) * 0.1;

    this.orbSizeMultiplier += (1.0 - this.orbSizeMultiplier) * 0.08;
    const idlePulse = Math.sin(this.tick * 0.02) * 0.015;
    const voicePulse = this.voiceIntensity * 0.35;
    const pulseScale = this.orbSizeMultiplier * (1 + idlePulse + voicePulse);
    this.currentOrbRadius = this.orbRadius * pulseScale;

    // 🎨 draw outer layers
    this.circles.forEach((swingpoints, k) => {
      swingpoints.forEach((point: any) => {
        point.phase += (Math.sin(this.tick / 100 + k) + Math.random() * 0.5)
          * 0.01 * randomness;

        const phase = 2 * Math.sin(this.tick / 85);
        const baseRadius = this.currentOrbRadius + 3;
        const displacement =
          point.range *
          phase *
          Math.sin(point.phase) *
          (0.5 + this.voiceIntensity * 2.5);
        const r = Math.max(
          this.currentOrbRadius * 0.85,
          Math.min(baseRadius + displacement * 0.8, this.currentOrbRadius * 1.4)
        );
        point.radian += (this.pi / 720) * rotationSpeed;
        point.x = this.center.x + r * Math.cos(point.radian);
        point.y = this.center.y + r * Math.sin(point.radian);
      });

      if (k === 0) ctx.globalCompositeOperation = 'multiply';
      else if (k === 1) ctx.globalCompositeOperation = 'screen';
      else ctx.globalCompositeOperation = 'overlay';

      ctx.filter = `blur(${1.5 + this.voiceIntensity * 1.5}px)`;

      const opacity = this.gradientOpacity;
      const gradient = this.createGradient(
        this.gradientAngle,
        layerStops.map(stop => ({
          position: stop.position,
          color: this.applyOpacity(stop.color, opacity),
        }))
      );
      this.drawCurve(swingpoints, gradient);
    });

    // 🌕 draw orb core
    ctx.filter = 'none';
    ctx.globalCompositeOperation = 'source-over';
    const coreGradient = this.createGradient(this.gradientAngle, coreStops);
    this.drawOrb(coreGradient);

    this.tick += pulseSpeed;
    this.animationId = requestAnimationFrame(this.animate);
  };

  private drawCurve(pts: any[], fillStyle: CanvasGradient) {
    const ctx = this.ctx;
    ctx.fillStyle = fillStyle;
    ctx.beginPath();
    ctx.moveTo(
      (pts[pts.length - 1].x + pts[0].x) / 2,
      (pts[pts.length - 1].y + pts[0].y) / 2
    );
    for (let i = 0; i < pts.length; i++) {
      const nextIndex = (i + 1) % pts.length;
      ctx.quadraticCurveTo(
        pts[i].x,
        pts[i].y,
        (pts[i].x + pts[nextIndex].x) / 2,
        (pts[i].y + pts[nextIndex].y) / 2
      );
    }
    ctx.closePath();
    ctx.fill();
  }

  private drawOrb(gradient: CanvasGradient) {
    const ctx = this.ctx;
    ctx.filter = `blur(${12 + this.voiceIntensity * 8}px)`;
    ctx.globalAlpha = 0.9 + this.voiceIntensity * 0.1;
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, this.currentOrbRadius, 0, this.pi * 2);
    ctx.fill();
    ctx.filter = 'none';
    ctx.globalAlpha = 1;
  }

  private createGradient(angle: number, stops: any[]) {
    const ctx = this.ctx;
    if (this.useRadialGradient) {
      const gradient = ctx.createRadialGradient(
        this.center.x, this.center.y, this.currentOrbRadius * 0.1,
        this.center.x, this.center.y, this.currentOrbRadius
      );
      stops.forEach((stop: any) =>
        gradient.addColorStop(stop.position / 100, stop.color)
      );
      return gradient;
    } else {
      const angleRad = ((angle - 90) * Math.PI) / 180;
      const startX = this.center.x - Math.cos(angleRad) * this.currentOrbRadius;
      const startY = this.center.y - Math.sin(angleRad) * this.currentOrbRadius;
      const endX = this.center.x + Math.cos(angleRad) * this.currentOrbRadius;
      const endY = this.center.y + Math.sin(angleRad) * this.currentOrbRadius;
      const gradient = ctx.createLinearGradient(startX, startY, endX, endY);
      stops.forEach((stop: any) =>
        gradient.addColorStop(stop.position / 100, stop.color)
      );
      return gradient;
    }
  }


  private random(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }


  
  /** ⚙️ Auto-generate stops for both gradients */
  private generateGradients() {
    if (!this.baseGradientColors || this.baseGradientColors.length === 0) return;
  
    const modes = {
      idle: {
        opacityFactor: 0.1,
        lightenAmount: 0.7,
      },
      thinking: {
        opacityFactor: 0.8,
        lightenAmount: 0.6,
      },
      writing: {
        opacityFactor: 1,
        lightenAmount: 0.4,
      },
    }
    
    
    
    // Apply to all modes if you want presets to use these new colors
    Object.keys(modes).forEach((mode) => {
      this.modeConfigs[mode].orbGradientStops = this.generateStops(this.baseGradientColors, modes[mode].opacityFactor);
      this.modeConfigs[mode].orbCoreStops = this.generateCoreStops(this.baseGradientColors, modes[mode].lightenAmount);
      console.log({
        mode,
        orbGradientStops: this.modeConfigs[mode].orbGradientStops,
        orbCoreStops: this.modeConfigs[mode].orbCoreStops,
      });
    });
  }
  


// Utility: adjust RGBA opacity dynamically
/** Utility: adjust RGBA/RGB/hex opacity robustly */
private adjustOpacity(color: string, factor: number): string {
  const c = this.parseColor(color);
  if (!c) return color;
  const newA = Math.min(1, Math.max(0, c.a * factor));
  // keep integer rgb channels, keep alpha with 2 decimals
  return `rgba(${Math.round(c.r)}, ${Math.round(c.g)}, ${Math.round(c.b)}, ${newA.toFixed(2)})`;
}

/** Utility: parse color string into rgba object; supports:
 *  - rgba(255, 0, 0, 0.5)
 *  - rgb(255, 0, 0)
 *  - rgb/rgba with spaces and slash: rgb(255 0 0 / 0.5)
 *  - hex (#fff, #ffffff)
 */
private parseColor(color: string): { r: number; g: number; b: number; a: number } | null {
  if (!color || typeof color !== 'string') return null;
  const s = color.trim();

  // hex: #fff or #ffffff
  if (s.startsWith('#')) {
    const hex = s.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return { r, g, b, a: 1 };
    } else if (hex.length === 6) {
      const bigint = parseInt(hex, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return { r, g, b, a: 1 };
    }
    return null;
  }

  // rgb/rgba variants: handle commas or spaces and optional slash for alpha
  const rgbRegex = /rgba?\(\s*([^\)]+)\s*\)/i;
  const m = s.match(rgbRegex);
  if (m) {
    const body = m[1].trim();
    // support either comma-separated or space-separated with optional slash
    let parts: string[] = [];
    if (body.indexOf(',') >= 0) {
      parts = body.split(',').map(p => p.trim());
    } else {
      // space separated; alpha may be after a slash
      const slashParts = body.split('/');
      if (slashParts.length === 2) {
        const rgbParts = slashParts[0].trim().split(/\s+/);
        parts = [...rgbParts, slashParts[1].trim()];
      } else {
        parts = body.split(/\s+/);
      }
    }

    const r = parseFloat(parts[0]);
    const g = parseFloat(parts[1]);
    const b = parseFloat(parts[2]);
    const a = parts[3] !== undefined ? parseFloat(parts[3]) : 1;
    if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)) {
      return { r, g, b, a: Number.isFinite(a) ? a : 1 };
    }
  }

  return null;
}

/** Utility: apply opacity to color (returns rgba string). Handles hex, rgb, rgba, modern rgb(...) / ... */
private applyOpacity(color: string, opacity: number): string {
  const parsed = this.parseColor(color);
  if (!parsed) return color;
  const { r, g, b } = parsed;
  const clamped = Math.min(1, Math.max(0, opacity));
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${clamped.toFixed(2)})`;
}

/** Utility: generate gradient stops evenly spaced using adjustOpacity */
private generateStops(colors: string[], opacityFactor: number) {
  if (!colors || colors.length === 0) return [];
  const step = colors.length === 1 ? 0 : 100 / (colors.length - 1);
  return colors.map((c, i) => ({
    position: Math.round(i * step),
    color: this.adjustOpacity(c, opacityFactor),
  }));
}

/** Utility: approximate a "core" color set from palette (lighter tones)
 *  Produces 3 stops: first, middle, last; returns rgb strings
 */
private generateCoreStops(colors: string[], lightenAmount = 0.35) {
  if (!colors || colors.length === 0) return [
    { position: 0, color: '#FFFFFF' },
    { position: 50, color: '#FFFFFF' },
    { position: 100, color: '#FFFFFF' },
  ];

  const pick = [
    colors[0],
    colors[Math.floor((colors.length - 1) / 2)],
    colors[colors.length - 1],
  ];

  const lightenColor = (col: string, amount = lightenAmount) => {
    const p = this.parseColor(col);
    if (!p) return '#FFFFFF';
    const l = (x: number) => Math.min(255, Math.round(x + (255 - x) * amount));
    return `rgb(${l(p.r)}, ${l(p.g)}, ${l(p.b)})`;
  };

  const result = pick.map((c, i) => ({
    position: Math.round(i * (100 / 2)), // 0,50,100
    color: lightenColor(c, lightenAmount),
  }));

  return result;
}


}